import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const TabsContext = createContext();

export const useTabs = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabs debe ser usado dentro de TabsProvider');
  }
  return context;
};

export const TabsProvider = ({ children }) => {
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [tabHeaders, setTabHeaders] = useState({}); // Store header content per tab
  const [tabOptions, setTabOptions] = useState({}); // Store options per tab

  const addTab = useCallback((tab) => {
    setTabs((prevTabs) => {
      // Verificar si la pestaña ya existe
      const existingTab = prevTabs.find(t => t.path === tab.path);
      if (existingTab) {
        setActiveTab(tab.path);
        return prevTabs;
      }
      // Agregar nueva pestaña
      const newTabs = [...prevTabs, { ...tab, id: Date.now() }];
      setActiveTab(tab.path);
      return newTabs;
    });
  }, []);

  const closeTab = useCallback((path) => {
    setTabs((prevTabs) => {
      const tabIndex = prevTabs.findIndex(t => t.path === path);
      const newTabs = prevTabs.filter(t => t.path !== path);
      
      // Si cerramos la pestaña activa, activar otra
      if (activeTab === path && newTabs.length > 0) {
        const newActiveIndex = tabIndex > 0 ? tabIndex - 1 : 0;
        setActiveTab(newTabs[newActiveIndex]?.path);
      } else if (newTabs.length === 0) {
        setActiveTab(null);
      }
      
      return newTabs;
    });
  }, [activeTab]);

  const switchTab = useCallback((path) => {
    setActiveTab(path);
  }, []);

  const setHeaderForTab = useCallback((path, header) => {
    setTabHeaders(prev => ({ ...prev, [path]: header }));
  }, []);

  const setOptionsForTab = useCallback((path, options) => {
    setTabOptions(prev => ({ ...prev, [path]: options }));
  }, []);

  // useMemo: evita que el objeto de contexto sea nuevo en cada render,
  // previniendo re-renders innecesarios en todos los consumidores de useTabs()
  const value = useMemo(() => ({
    tabs,
    activeTab,
    addTab,
    closeTab,
    switchTab,
    tabHeaders,
    tabOptions,
    setHeaderForTab,
    setOptionsForTab,
  }), [tabs, activeTab, addTab, closeTab, switchTab, tabHeaders, tabOptions, setHeaderForTab, setOptionsForTab]);

  return (
    <TabsContext.Provider value={value}>
      {children}
    </TabsContext.Provider>
  );
};
